import React from 'react';
import { DeleteOutlined } from '@ant-design/icons';
import { Dropdown, Menu, message, Modal } from 'antd';
import { EntityType } from '../../types.generated';
import { useEntityRegistry } from '../useEntityRegistry';
import { useDeleteDomainMutation } from '../../graphql/domain.generated';
import { MenuIcon } from '../entity/shared/EntityDropdown/EntityDropdown';
import { useTranslation } from '../../i18n/useTranslation';
import { useIsEntityAttachedToReadOnlyDatasetsLazyQuery } from '../../graphql/entity.generated';
import { checkEntityAttachedToReadOnlyDatasets } from '../shared/deleteUtils';

type Props = {
    urn: string;
    name: string;
    onDelete?: () => void;
};

export default function DomainItemMenu({ name, urn, onDelete }: Props) {
    const { t } = useTranslation();
    const entityRegistry = useEntityRegistry();
    const [deleteDomainMutation] = useDeleteDomainMutation();
    const [checkIsEntityAttachedToReadOnlyDatasets] = useIsEntityAttachedToReadOnlyDatasetsLazyQuery();

    const deleteDomain = () => {
        deleteDomainMutation({
            variables: {
                urn,
            },
        })
            .then(({ errors }) => {
                if (!errors) {
                    message.success(t('Deleted Domain!'));
                    onDelete?.();
                }
            })
            .catch(() => {
                message.destroy();
                message.error({ content: t('Failed to delete Domain!: An unknown error occurred.'), duration: 3 });
            });
    };

    const onConfirmDelete = async () => {
        const canDelete = await checkEntityAttachedToReadOnlyDatasets({
            entityRegistry,
            entityType: EntityType.Domain,
            entityData: { name, urn },
            checkFunction: async () => {
                const result: any = await checkIsEntityAttachedToReadOnlyDatasets({
                    variables: {
                        urn,
                    },
                });
                return result;
            },
            getModalTitle: (displayName: string) => t('Cannot Delete Domain {{name}}', { name: displayName }),
            getModalContent: (entityName: string) =>
                t('This {{entityName}} is attached to read-only datasets and cannot be deleted.', {
                    entityName: t(entityName || ''),
                }),
            getModalOkText: () => t('Ok'),
        });

        if (!canDelete) {
            return;
        }

        Modal.confirm({
            title: t('Delete Domain {{name}}', { name }),
            content: t('Are you sure you want to delete this {{entityName}}?', {
                entityName: t(entityRegistry.getEntityName(EntityType.Domain) || ''),
            }),
            onOk() {
                deleteDomain();
            },
            centered: true,
            onCancel() {},
            okText: t('Delete'),
            cancelText: t('Cancel'),
            maskClosable: true,
            closable: true,
        });
    };

    return (
        <Dropdown
            trigger={['click']}
            overlay={
                <Menu>
                    <Menu.Item onClick={onConfirmDelete} key="delete">
                        <DeleteOutlined /> &nbsp;{t('Delete')}
                    </Menu.Item>
                </Menu>
            }
        >
            <MenuIcon data-testid={`dropdown-menu-${urn}`} fontSize={20} />
        </Dropdown>
    );
}
